/**
 * Sidebar Context
 *
 * Provides sidebar collapse state across the entire application
 * without prop drilling.
 *
 * Usage:
 * // In App.tsx (wrap your app):
 * <SidebarProvider>
 *   <App />
 * </SidebarProvider>
 *
 * // In any component:
 * const { isCollapsed, toggle } = useSidebar();
 */
import { createContext, useContext, ReactNode } from 'react';
import { useSidebarCollapse } from '../hooks/useSidebarCollapse';

interface SidebarContextType {
  isCollapsed: boolean;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const sidebarState = useSidebarCollapse();

  return (
    <SidebarContext.Provider value={sidebarState}>
      {children}
    </SidebarContext.Provider>
  );
};

/**
 * Hook to use sidebar context
 *
 * Must be used within SidebarProvider
 */
export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
